import { promises as fs } from 'node:fs';
import path from 'node:path';
import { ValidationError } from '../errors/index.js';

export interface PrimaryPackageMetadata {
    tag: string;
    kind?: 'primary' | 'addon';
    conflictsWith?: string[];
}

export interface PrimaryPackageCompatibilityOptions {
    projectRoot?: string;
    requestedPackages?: string[];
    requestedPaths?: string[];
    packageMetadata?: PrimaryPackageMetadata[];
}

const PRIMARY_RULES = new Map<string, string>([
    ['core', 'rules/jai1.md'],
    ['core-v2', 'rules/jai1-v2.md'],
    ['core-v3', 'rules/jai1-v3.md'],
    ['hive-v1', 'rules/jai1-hive.md'],
    ['hive-v2', 'rules/jai1-hive2.md'],
]);

const PRIMARY_BY_RULE = new Map(
    Array.from(PRIMARY_RULES, ([packageName, rulePath]) => [rulePath, packageName]),
);

export async function detectInstalledPrimaryPackage(
    projectRoot: string = process.cwd(),
): Promise<string | null> {
    const detected = new Set<string>();
    const statePackages = await readProjectStatePrimaries(projectRoot);
    for (const packageName of statePackages) detected.add(packageName);

    const manifest = await readJsonRecord(path.join(projectRoot, '.jai1', 'manifest.json'));
    const manifestPaths = new Set(Object.keys(manifest));
    for (const value of Object.values(manifest)) {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            const filepath = (value as Record<string, unknown>).filepath;
            if (typeof filepath === 'string') manifestPaths.add(filepath);
        }
    }
    for (const filepath of manifestPaths) {
        const packageName = PRIMARY_BY_RULE.get(normalizeComponentPath(filepath));
        if (packageName) detected.add(packageName);
    }

    for (const [packageName, rulePath] of PRIMARY_RULES) {
        if (await pathExists(path.join(projectRoot, '.jai1', rulePath))) {
            detected.add(packageName);
        }
    }

    if (detected.size > 1) {
        throw new ValidationError(
            `Phát hiện nhiều primary package đã cài: ${Array.from(detected).join(', ')}. ` +
            'Hãy dọn trạng thái Jai1 cũ trước khi apply.',
        );
    }

    return detected.values().next().value ?? null;
}

export async function assertPrimaryPackageCompatibility(
    options: PrimaryPackageCompatibilityOptions,
): Promise<void> {
    const requested = detectRequestedPrimaryPackages(options);
    const requestedIdentities = new Set(options.requestedPackages ?? []);
    const metadataByTag = new Map((options.packageMetadata ?? []).map((item) => [item.tag, item]));

    for (const tag of requestedIdentities) {
        const conflictingSelection = metadataByTag.get(tag)?.conflictsWith?.find((conflict) => requestedIdentities.has(conflict));
        if (conflictingSelection) {
            throw new ValidationError(`Không thể apply đồng thời package '${tag}' và '${conflictingSelection}' vì chúng xung đột.`);
        }
    }

    if (requested.size > 1) {
        throw new ValidationError(
            `Chỉ được apply một primary package. Đã chọn: ${Array.from(requested).join(', ')}.`,
        );
    }

    const requestedPrimary = requested.values().next().value;
    const hasDeclaredConflicts = [...requestedIdentities]
        .some((tag) => (metadataByTag.get(tag)?.conflictsWith?.length ?? 0) > 0);
    if (!requestedPrimary && !hasDeclaredConflicts) return;
    const installedPrimary = await detectInstalledPrimaryPackage(options.projectRoot ?? process.cwd());
    const metadataConflict = installedPrimary
        ? [...requestedIdentities].find((tag) => metadataByTag.get(tag)?.conflictsWith?.includes(installedPrimary))
        : undefined;

    if (installedPrimary && (metadataConflict || (requestedPrimary && installedPrimary !== requestedPrimary))) {
        const requestedPackage = metadataConflict ?? requestedPrimary;
        if (!requestedPackage) return;
        throw new ValidationError(
            `Project đang dùng primary package '${installedPrimary}', không thể apply '${requestedPackage}'. ` +
            'Mỗi project chỉ được dùng một primary package. ' +
            'Hãy sao lưu thay đổi local, gỡ primary package hiện tại khỏi .jai1 rồi apply lại package mới; CLI sẽ không tự xóa hoặc ghi đè.',
        );
    }
}

function detectRequestedPrimaryPackages(
    options: PrimaryPackageCompatibilityOptions,
): Set<string> {
    const requested = new Set<string>();
    const metadataByTag = new Map((options.packageMetadata ?? []).map((item) => [item.tag, item]));

    for (const packageName of options.requestedPackages ?? []) {
        const metadata = metadataByTag.get(packageName);
        if (metadata?.kind === 'primary'
            || (metadata?.kind !== 'addon' && PRIMARY_RULES.has(packageName))) {
            requested.add(packageName);
        }
    }

    for (const filepath of options.requestedPaths ?? []) {
        const packageName = PRIMARY_BY_RULE.get(normalizeComponentPath(filepath));
        if (packageName && metadataByTag.get(packageName)?.kind !== 'addon') requested.add(packageName);
    }

    return requested;
}

async function readProjectStatePrimaries(projectRoot: string): Promise<string[]> {
    const projectStatePath = path.join(projectRoot, '.jai1', 'project.json');
    let state: Record<string, unknown>;
    try {
        const parsed: unknown = JSON.parse(await fs.readFile(projectStatePath, 'utf-8'));
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('expected object');
        state = parsed as Record<string, unknown>;
    } catch (error) {
        if ((error as NodeJS.ErrnoException).code === 'ENOENT') return [];
        throw new ValidationError(`.jai1/project.json không hợp lệ: ${error instanceof Error ? error.message : String(error)}`);
    }
    const coreV3 = state.coreV3 && typeof state.coreV3 === 'object' && !Array.isArray(state.coreV3)
        ? state.coreV3 as Record<string, unknown>
        : null;
    const values = [state.primaryPackage, coreV3?.primaryPackage]
        .filter((value) => value !== undefined);
    const packages: string[] = [];

    for (const value of values) {
        if (typeof value !== 'string' || !PRIMARY_RULES.has(value)) {
            throw new ValidationError(`Primary package identity '${String(value)}' không hợp lệ trong .jai1/project.json.`);
        }
        packages.push(value);
    }

    return packages;
}

async function readJsonRecord(filepath: string): Promise<Record<string, unknown>> {
    try {
        const value = JSON.parse(await fs.readFile(filepath, 'utf-8'));
        return value && typeof value === 'object' && !Array.isArray(value)
            ? value as Record<string, unknown>
            : {};
    } catch {
        return {};
    }
}

async function pathExists(filepath: string): Promise<boolean> {
    try {
        await fs.access(filepath);
        return true;
    } catch {
        return false;
    }
}

function normalizeComponentPath(filepath: string): string {
    return filepath.replace(/\\/g, '/').replace(/^\.jai1\//, '');
}
